import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import variables from "@/config/variables";
import "./PlaidTestControls.scss";

export type PlaidTestState = "idle" | "loading" | "success" | "failed";

export interface PlaidTestControlsProps {
  onStateChange: (newState: PlaidTestState) => void;
}

export const PlaidTestControls: React.FC<PlaidTestControlsProps> = ({ onStateChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [testState, setTestState] = useState<PlaidTestState>("idle");

  // Handle test state changes
  const handleTestStateChange = (newState: PlaidTestState) => {
    setTestState(newState);
    onStateChange(newState);
  };

  // Dragging functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if clicking on interactive elements
    if (
      (e.target as HTMLElement).tagName === "SELECT" ||
      (e.target as HTMLElement).tagName === "OPTION" ||
      (e.target as HTMLElement).closest("select")
    ) {
      return;
    }

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Only show if enabled via configuration
  if (!variables.enablePlaidTestControls) return null;

  return (
    <div
      className="plaid-test-controls"
      style={{
        top: position.y,
        left: position.x,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="plaid-test-controls__header">
        <FontAwesomeIcon icon={faGripVertical} className="plaid-test-controls__grip-icon" />
        <span className="plaid-test-controls__title">Plaid Test Controls</span>
      </div>
      <div className="plaid-test-controls__content">
        <label htmlFor="test-state" className="plaid-test-controls__label">
          State:
        </label>
        <select
          id="test-state"
          value={testState}
          onChange={(e) => handleTestStateChange(e.target.value as PlaidTestState)}
          className="plaid-test-controls__select"
        >
          <option value="idle">Idle</option>
          <option value="loading">Loading</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
      </div>
    </div>
  );
};
